import type { ApiClient } from "../../../infrastructure/http/apiClient";
import { ApiClientError } from "../../../infrastructure/http/apiClient";
import type { BillingCapabilities } from "../domain/capabilities";

const BILLING_CAPABILITIES_PATH = "/api/v1/billing/capabilities";

export async function getBillingCapabilities(apiClient: ApiClient): Promise<BillingCapabilities> {
  const response = await apiClient.get<unknown>(BILLING_CAPABILITIES_PATH);

  if (!isBillingCapabilities(response)) {
    throw new ApiClientError("The billing capabilities response did not match the expected contract.", {
      code: "invalid_response"
    });
  }

  return response;
}

function isBillingCapabilities(value: unknown): value is BillingCapabilities {
  if (!isRecord(value) || typeof value.tenant_id !== "string") {
    return false;
  }

  if (!isBooleanMap(value.features) || !isNumberMap(value.limits)) {
    return false;
  }

  if (!isRecord(value.billing) || typeof value.billing.status !== "string") {
    return false;
  }

  return optionalString(value.billing.plan_id)
    && optionalString(value.billing.offer_id)
    && optionalString(value.billing.offer_code)
    && optionalString(value.billing.catalog_id)
    && optionalActivation(value.billing.activation);
}

function isBooleanMap(value: unknown): value is Record<string, boolean> {
  return isRecord(value) && Object.values(value).every((entry) => typeof entry === "boolean");
}

function isNumberMap(value: unknown): value is Record<string, number> {
  return isRecord(value) && Object.values(value).every((entry) => typeof entry === "number");
}

function optionalString(value: unknown): boolean {
  return value === undefined || typeof value === "string";
}

function optionalActivation(value: unknown): boolean {
  if (value === undefined) {
    return true;
  }

  if (!isRecord(value)
    || typeof value.state !== "string"
    || typeof value.payment_method_requirement !== "string"
    || typeof value.payment_method_status !== "string"
    || !optionalString(value.block_reason)) {
    return false;
  }

  if (value.trial === undefined) {
    return true;
  }

  return isRecord(value.trial)
    && (value.trial.duration_days === undefined || typeof value.trial.duration_days === "number");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
